import {
  RiArrowGoBackLine,
  RiCalendarCloseLine,
  RiCalendarScheduleLine,
  RiCheckDoubleLine,
  RiCloseCircleLine,
  RiMap2Line,
  RiMapPin2Line,
  RiTimeLine,
  RiTruckLine,
  RiWalletLine,
} from "@remixicon/react";
import { NavigateFunction } from "react-router-dom";
import { getCookie } from "typescript-cookie";
import { CurrentActiveUserInterface } from "../constants/User";
import {
  axiosBuyer,
  axiosPublic,
  axiosReseller,
  axiosSupplier,
} from "./AxiosInstance";
import { UAParser } from "ua-parser-js";
import { SHA256 } from "crypto-js";

export const RegexURL =
  /^((ftp|http|https):\/\/)?(www.)?(?!.*(ftp|http|https|www.))[a-zA-Z0-9_-]+(\.[a-zA-Z]+)+((\/)[\w#]+)*(\/\w+\?[a-zA-Z0-9_]+=\w+(&[a-zA-Z0-9_]+=\w+)*)?$/gm;

export const invalidCharsUrl: string[] = [
  " ",
  "<",
  ">",
  "#",
  "%",
  "{",
  "}",
  "|",
  "\\",
  "^",
  "[",
  "]",
  "`",
  '"',
  "&",
  "+",
  "=",
  "*",
  "@",
  "!",
  "~",
  "$",
  "/",
];

export const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export const getTitleCase = (str: string) => {
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const formatDate = (value: string): string => {
  if (!value) {
    return "Invalid Date";
  }

  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return "Invalid Date";
  }
  const formattedDate = new Intl.DateTimeFormat("id-ID", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);

  return formattedDate;
};

export const formatDateToDayMonthYear = (value: string): string => {
  const date = new Date(value);

  const options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "long",
    year: "numeric",
  };

  return new Intl.DateTimeFormat("id-ID", options).format(date);
};

export const getEstimatedArrivalDate = (estimatedDevlivery?: string) => {
  const [minDays, maxDays] = estimatedDevlivery?.split("-").map(Number) ?? [];
  const currentDate = new Date();

  const minDate = new Date(currentDate);
  minDate?.setDate(currentDate?.getDate() + minDays);

  const maxDate = new Date(currentDate);
  maxDate?.setDate(currentDate?.getDate() + maxDays);

  const formatOptionsDay = { day: "numeric" } as Intl.DateTimeFormatOptions;
  const formatOtionsDayMonth = {
    day: "numeric",
    month: "long",
  } as Intl.DateTimeFormatOptions;

  const formatedMinDate =
    minDate?.getMonth() === maxDate.getMonth()
      ? minDate.toLocaleDateString("id-ID", formatOptionsDay)
      : minDate?.toLocaleDateString("id-ID", formatOtionsDayMonth);
  const formatedMaxDate = maxDate.toLocaleDateString(
    "id-ID",
    formatOtionsDayMonth
  );

  return `${formatedMinDate} - ${formatedMaxDate}`;
};

export const formatTime = (value: string): string => {
  if (!value) {
    return "Invalid Date";
  }

  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return "Invalid Date";
  }
  const formattedDate = new Intl.DateTimeFormat("id-ID", {
    hour: "numeric",
    minute: "numeric",
  }).format(date);

  return formattedDate;
};

const formatNumber = (numParam: number | string) => {
  if (numParam === 0) {
    return 0;
  }
  let num: number;

  if (typeof numParam === "string") {
    num = parseInt(numParam.replace(/[^0-9]/g, ""), 10);
  } else {
    num = numParam;
  }

  if (!num) {
    return "";
  }

  return num.toLocaleString("id-ID");
};
export default formatNumber;

export const formatToRupiah = (price: number) => {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

export const sortingData = (
  data: any[],
  columnKey: string,
  direction: "asc" | "desc",
  isDate: boolean = false
) => {
  return [...data].sort((a, b) => {
    const valA = a[columnKey];
    const valB = b[columnKey];

    if (isDate) {
      const dateA = new Date(valA).getTime();
      const dateB = new Date(valB).getTime();
      return direction === "asc" ? dateA - dateB : dateB - dateA;
    }

    if (typeof valA === "string" && typeof valB === "string") {
      return direction === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    }

    return 0;
  });
};

export function sortDataTable<T>(
  data: T[],
  columnKey: keyof T,
  direction: "asc" | "desc"
): T[] {
  return data.slice().sort((a, b) => {
    let aValue = a[columnKey];
    let bValue = b[columnKey];

    if (typeof aValue === "string" && !isNaN(Date.parse(aValue))) {
      aValue = new Date(aValue).getTime() as unknown as T[keyof T];
      bValue = new Date(bValue as string).getTime() as unknown as T[keyof T];
    }

    if (typeof aValue === "number" && typeof bValue === "number") {
      return direction === "asc"
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    }

    if (typeof aValue === "string" && typeof bValue === "string") {
      return direction === "asc"
        ? aValue.toLowerCase().localeCompare(bValue.toLowerCase())
        : bValue.toLowerCase().localeCompare(aValue.toLowerCase());
    }

    return 0;
  });
}

export const capitalizeFirstLetter = (val: string) => {
  return String(val).charAt(0).toUpperCase() + String(val).slice(1);
};

export const getAxiosInstance = (pageType: number) => {
  switch (pageType) {
    case 1:
      return axiosBuyer;
    case 2:
      return axiosReseller;
    case 3:
      return axiosSupplier;
    default:
      throw new Error("Invalid page type provided");
  }
};

export const cleanName = (data: string) => {
  const final = data[0].toUpperCase() + data.slice(1).toLowerCase();
  return final;
};

export const capitalizeWords = (data: string): string => {
  return data
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const splitCityName = (data: string): string => {
  let val: string = "";

  if (data.includes("KABUPATEN")) {
    val = data.split("KABUPATEN").pop() || "";
  } else if (data.includes("KOTA")) {
    val = data.split("KOTA").pop() || "";
  } else {
    val = data;
  }

  return capitalizeWords(val.trim());
};

export const hasWhiteSpace = (data: string) => {
  return data.indexOf(" ") >= 0;
};

export const isValidUrl = (value: string) => {
  const urlPattern = new RegExp(
    "^(https?:\\/\\/)?" +
      "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" +
      "((\\d{1,3}\\.){3}\\d{1,3}))" +
      "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" +
      "(\\?[;&a-z\\d%_.~+=-]*)?" +
      "(\\#[-a-z\\d_]*)?$",
    "i"
  );
  return !!urlPattern.test(value);
};

export const explodeString = (data?: string): { id: string; nama: string } => {
  if (!data) {
    return { id: "", nama: "" };
  }

  const [id, nama] = data.split("|");
  return { id, nama };
};

export const changeToComa = (data: string) => {
  const cleanData = data
    .replace(/[\d,]+/g, "")
    .replace(/\s+/g, "")
    .trim();

  const result = cleanData.split("|");
  const finalResult = result.join(", ");

  return finalResult;
};

export const copyUrl = async (data: string): Promise<number> => {
  let status: number = 0;
  let urlToCopy = `${data}`;

  try {
    await navigator.clipboard.writeText(urlToCopy);
    status = 1;
  } catch {
    status = 0;
  }

  return status;
};

export const getCurrentActiveUser = () => {
  const getCurrentUserCookie =
    getCookie("currentActiveUser") ||
    `{"id":null, "name":null, "role":null, "unique_id":null}`;
  const currentActiveUser = JSON.parse(getCurrentUserCookie);
  return currentActiveUser;
};

export const checkTokens = (): number => {
  const buyerToken = getCookie("buyerToken");
  const resellerToken = getCookie("resellerToken");
  const supplierToken = getCookie("supplierToken");

  if (buyerToken) {
    return 1;
  }

  if (resellerToken || supplierToken) {
    return 0;
  }

  return 3;
};

export const getBuyerToken = () => {
  const buyerToken = getCookie("buyerToken");
  return buyerToken;
};

export const validateDate = (dateValue: string) => {
  const now = new Date();
  const value = new Date(dateValue);

  return now > value;
};

export const getTodayDate = () => {
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
};

export const goToDashboard = (navigate: NavigateFunction) => {
  const currentUser: CurrentActiveUserInterface = getCurrentActiveUser();

  if (currentUser) {
    if (currentUser.role === "buyer") {
      navigate("/buyer");
    } else if (currentUser.role === "reseller") {
      navigate("/reseller");
    } else if (currentUser.role === "supplier") {
      navigate("/supplier");
    }
  }
};

export const saveFcmTokenToBackend = async (
  id: any,
  role: string,
  token: string | null
) => {
  if (!token) return;

  try {
    const payload = {
      id: id,
      role: role,
      token: token,
    };

    const response = await axiosPublic.post(
      `/v1/notification/save-fcm-token`,
      payload
    );

    return response.data.data;
  } catch (error) {
    console.error("Error saving token to backend:", error);
  }
};

export const requestPermissionNotification = () => {
  if ("Notification" in window) {
    Notification.requestPermission();
  }
};

export const checkPermissionNotification = () => {
  if ("Notification" in window) {
    if (Notification.permission === "default") {
      requestPermissionNotification();
    }
  }
};

export const dontAllowModalPermission = () => {
  localStorage.setItem("dontShowNotificationModal", JSON.stringify(true));
};

export const getModalPermission = () => {
  const valLocalStorage = localStorage.getItem("dontShowNotificationModal");
  const dontShowNotificationModal =
    valLocalStorage !== null ? JSON.parse(valLocalStorage) : null;

  const isNotificationSupported = "Notification" in window;

  if (
    isNotificationSupported &&
    Notification.permission === "default" &&
    (dontShowNotificationModal === false || dontShowNotificationModal === null)
  ) {
    return true;
  }

  return false;
};

export const isUuid = (input: string): boolean => {
  const uuidRegex =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  return uuidRegex.test(input);
};

export const isDueDatePassed = (dueDate: string) => {
  const dueDateTime = new Date(dueDate).getTime();
  const currentTime = Date.now();
  return currentTime > dueDateTime;
};

export const statusOrderNote = (status: number, unpaid: boolean = false) => {
  if (status === 0 && !unpaid) {
    return { icon: RiCalendarScheduleLine, color: "teal.400" };
  } else if (status === 1 && !unpaid) {
    return { icon: RiCheckDoubleLine, color: "blue.400" };
  } else if (status === 2 && !unpaid) {
    return { icon: RiArrowGoBackLine, color: "red.400" };
  } else if (status === 3 && !unpaid) {
    return { icon: RiCloseCircleLine, color: "red.400" };
  } else if (status === 4 && !unpaid) {
    return { icon: RiTruckLine, color: "yellow.400" };
  } else if (status === 5 && !unpaid) {
    return { icon: RiMapPin2Line, color: "green.400" };
  } else if (status === 0 && unpaid) {
    return { icon: RiWalletLine, color: "yellow.400" };
  } else if (status === 1 && unpaid) {
    return { icon: RiTimeLine, color: "blue.400" };
  } else if (status === 2 && unpaid) {
    return { icon: RiCheckDoubleLine, color: "green.400" };
  } else if (status === 3 && unpaid) {
    return { icon: RiCloseCircleLine, color: "red.400" };
  } else if (status === 4 && unpaid) {
    return { icon: RiCalendarCloseLine, color: "red.400" };
  } else {
    return { icon: RiMap2Line, color: "blue.400" };
  }
};

export const chatSupplier = (
  supplierId: any,
  productId: any,
  variantId: any,
  storePrefix: string | undefined,
  slug: string | undefined,
  navigate: NavigateFunction
) => {
  const url = `/buyer/chat?supplierId=${supplierId}&productId=${productId}&variantId=${variantId}&storePrefix=${storePrefix}&slug=${slug}`;
  navigate(url);
};

export const getIdBuyer = () => {
  const getCurrentUserCookies = getCookie("currentActiveUser") || null;
  const getLoggedUsers = getCookie("loggedUsers") || null;
  const cookieUser: CurrentActiveUserInterface = getCurrentUserCookies
    ? JSON.parse(getCurrentUserCookies)
    : null;
  const cookieLoggedUsers: CurrentActiveUserInterface[] = getLoggedUsers
    ? JSON.parse(getLoggedUsers)
    : null;

  const buyerResult = cookieLoggedUsers.find(
    (user: CurrentActiveUserInterface) => user.role === "buyer"
  );

  return buyerResult ? buyerResult : undefined;
};

export const createVisitorId = async () => {
  const parser = new UAParser();
  const userAgent = parser.getUA();
  console.log("User-Agent:", userAgent);

  const device = parser.getDevice();

  const browser = parser.getBrowser();

  const os = parser.getOS();

  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const day = now.getDate();
  const hour = now.getHours();
  const minute = now.getMinutes();
  const second = now.getSeconds();
  const millisecond = now.getMilliseconds();

  const dataId = `${device.type || "desktop"}|${device.vendor || ""}|${
    device.model || ""
  }|${browser.name || ""}|${browser.version || ""}|${os.name || ""}|${
    os.version || ""
  }|${year}|${month}|${day}|${hour}|${minute}|${second}|${millisecond}`;
  const hashedString = await SHA256(dataId).toString();
  localStorage.setItem("visitorId", hashedString);

  return hashedString;
};

export const getVisitorId = async () => {
  let visitorId = localStorage.getItem("visitorId");

  if (!visitorId) {
    visitorId = await createVisitorId();
  }

  return visitorId;
};
